import axios from 'axios';

// Check if we should use Cloudflare Workers API
// Auto-detect production and development environments
const isProduction = window.location.hostname === 'bacchus-client.pages.dev';
const isDevelopment = window.location.hostname === 'bacchus-client-dev.pages.dev';
const isCloudflarePages = isProduction || isDevelopment;

const useCloudflare = process.env.REACT_APP_USE_CLOUDFLARE === 'true' || isCloudflarePages;

// Auto-select API URL based on environment
const getDefaultApiUrl = () => {
  if (isDevelopment) return 'https://bacchus-api-dev.sicetor9.workers.dev/api';
  if (isProduction) return 'https://bacchus-api.sicetor9.workers.dev/api';
  return 'https://bacchus-api.sicetor9.workers.dev/api';
};

const API_BASE_URL = useCloudflare 
  ? (process.env.REACT_APP_API_URL || getDefaultApiUrl())
  : (process.env.REACT_APP_API_BASE_URL || '/api');

// State for API mode switching
let useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true' || false;

console.log('API Configuration:', {
  useCloudflare,
  API_BASE_URL,
  useMockData
});

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Request interceptor to handle mock data switching
api.interceptors.request.use(
  (config) => {
    // Modify URL based on mock data setting
    if (useMockData && !config.url.startsWith('/mock/')) {
      // Convert regular API calls to mock API calls
      if (config.url.includes('/actresses')) {
        config.url = config.url.replace('/actresses', '/mock/actresses');
      } else if (config.url.includes('/videos')) {
        config.url = config.url.replace('/videos', '/mock/videos');
      }
    }
    
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      mockMode: useMockData,
      params: config.params
    });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and logging
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status}`, {
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });
    return Promise.reject(error);
  }
);

// API configuration functions
export const apiConfig = {
  // Get current mock data setting
  isUsingMockData() {
    return useMockData;
  },
  
  // Set mock data usage
  setUseMockData(enabled) {
    useMockData = enabled;
    localStorage.setItem('useMockData', enabled.toString());
    console.log(`Mock data mode ${enabled ? 'enabled' : 'disabled'}`);
  },
  
  // Load setting from localStorage
  loadMockDataSetting() {
    const saved = localStorage.getItem('useMockData');
    if (saved !== null) {
      useMockData = saved === 'true';
    }
    return useMockData;
  },
  
  // Get API instance
  getApi() {
    return api;
  }
};

// Actress API endpoints
export const actressApi = {
  getAll(params = {}) {
    return api.get('/actresses', { params });
  },
  
  getById(id) {
    return api.get(`/actresses/${id}`);
  },
  
  getEnhanced(id) {
    return api.get(`/actresses/${id}/enhanced`);
  },
  
  getProfile(id) {
    return api.get(`/actresses/${id}/profile`);
  },
  
  syncDmm(id) {
    return api.post(`/actresses/${id}/sync-dmm`);
  },
  
  search(query, params = {}) {
    return api.get('/actresses/search', { 
      params: { q: query, ...params } 
    });
  }
};

// Video API endpoints
export const videoApi = {
  getAll(params = {}) {
    return api.get('/videos', { params });
  },
  
  get(path) {
    return api.get(`/videos${path}`);
  },
  
  getById(id) {
    return api.get(`/videos/${id}`);
  },
  
  search(query, params = {}) {
    return api.get('/videos/search', { 
      params: { q: query, ...params } 
    });
  }
};

// Mock data specific endpoints
export const mockApi = {
  regenerateData() {
    return api.post('/mock/regenerate');
  },
  
  getStats() {
    return api.get('/mock/stats');
  }
};

// MGS API endpoints
export const mgsApi = {
  search(keyword, options = {}) {
    return api.get('/mgs/search', {
      params: {
        keyword,
        page: options.page || 1
      }
    });
  },
  
  import(productCode) {
    return api.post('/mgs/import', { productCode });
  },
  
  batchImport(productCodes) {
    return api.post('/mgs/batch-import', { productCodes });
  },
  
  getVideo(productCode) {
    return api.get(`/mgs/video/${productCode}`);
  },
  
  getNewReleases() {
    return api.get('/mgs/new-releases');
  }
};

// DMM API endpoints
export const dmmApi = {
  search(keyword, options = {}) {
    if (useCloudflare) {
      // Cloudflare Workers uses GET with query params
      return api.get('/dmm/search', {
        params: {
          keyword,
          hits: options.hits || 20,
          offset: options.offset || 0
        }
      });
    }
    return api.post('/videos/dmm/search', {
      keyword,
      hits: options.hits || 20,
      offset: options.offset || 0,
      saveToDb: options.saveToDb || false
    });
  },
  
  importByProductCode(productCode) {
    if (useCloudflare) {
      return api.post('/dmm/import', { productCode });
    }
    return api.post(`/videos/dmm/import/${productCode}`);
  },
  
  batchImport(productCodes) {
    if (useCloudflare) {
      // Cloudflare doesn't have batch import, do sequential
      const promises = productCodes.map(code => 
        api.post('/dmm/import', { productCode: code })
      );
      return Promise.allSettled(promises).then(results => ({
        data: {
          success: results.filter(r => r.status === 'fulfilled').map(r => r.value.data),
          errors: results.filter(r => r.status === 'rejected').map((r, i) => ({
            productCode: productCodes[i],
            error: r.reason.message
          }))
        }
      }));
    }
    return api.post('/videos/dmm/batch-import', { productCodes });
  },
  
  getStatus() {
    return api.get(useCloudflare ? '/dmm/status' : '/videos/dmm/status');
  },
  
  syncVideo(videoId) {
    if (useCloudflare) {
      // Cloudflare Workers doesn't have sync endpoint, use import instead
      return api.get(`/videos/${videoId}`).then(res => {
        if (res.data.productCode) {
          return api.post('/dmm/import', { productCode: res.data.productCode });
        }
        throw new Error('No product code found');
      });
    }
    return api.put(`/videos/${videoId}/sync-dmm`);
  }
};

// Initialize on load
apiConfig.loadMockDataSetting();

export default api;